import React, { useReducer, useEffect, useState } from 'react'
import { omit, cloneDeep, groupBy, flatten } from 'lodash'
import { Icon, IconButton, Button, LocationSelect, Toaster, ListWrapper } from '@ui'
import { Classes, Collapse } from '@blueprintjs/core'
import api, { IFloorWithBuilding, IFloor } from '@api'
import { ApolloError } from 'apollo-boost'
import { pluralize } from '@utils'
import { showLocationsList } from './EmployeeDetailsDialog'

enum Actions {
  LOAD,
  UPDATE,
}

interface IManagedBuilding {
  id: string
  name: string
  address: string
  region: string
  floors: IFloor[]
}

interface IState {
  loading: boolean
  groupedFloors: { [region: string]: IManagedBuilding[] } | []
  managedFloors: IFloorWithBuilding[]
}

interface Action {
  type: Actions.LOAD | Actions.UPDATE
  groupedFloors: { [region: string]: IManagedBuilding[] } | []
  managedFloors: IFloorWithBuilding[]
}

export default function ManagedLocationsList({
  readOnly,
  employeeId,
  role,
  employee,
}: {
  readOnly: boolean
  employeeId: string
  role: string
  employee: any
}) {
  const { loading, groupedFloors, managedFloors, addFloor, deleteFloor } = useManagedLocations(
    employeeId,
    role
  )

  // Do not show mangeged locaitons section when list is empty
  if (readOnly && !groupedFloors.length) {
    return null
  }

  return (
    <div>
      {showLocationsList(employee) && (
        <div className="mt-3 flex flex-col">
          <div className="font-semibold mb-3" style={{ fontSize: 14 }}>
            Manage locations
          </div>
          {Object.entries(groupedFloors).map(([region, buildings]) => (
            <div key={region}>
              <div className={`my-3 ${loading ? Classes.SKELETON : ''}`}>{region}</div>
              {buildings.map((building) => (
                <RegionBuilding
                  key={building.id}
                  building={building}
                  deleteFloor={deleteFloor}
                  isReadOnly={readOnly}
                  employeeId={employeeId}
                  loading={loading}
                />
              ))}
            </div>
          ))}
          {!readOnly && (
            <LocationSelect
              mode={LocationSelect.Mode.SINGLE_FLOOR}
              className="mt-2"
              value={null}
              checkValues={managedFloors.map(({ id }) => id)}
              onChange={(floor: any) => {
                addFloor(floor)
                api.employee
                  .addFloorToManagedLocations({
                    floorPlanId: floor.id,
                    id: employeeId,
                  })
                  .then(() => Toaster.success({ message: 'Floor added' }))
                  .catch((error: ApolloError) => Toaster.fail({ error }))
              }}
              target={<Button minimal blue text="Add locations" iconName="plus" />}
            />
          )}
        </div>
      )}
    </div>
  )
}

function RegionBuilding({
  building,
  isReadOnly,
  deleteFloor,
  employeeId,
  loading,
}: {
  building: IManagedBuilding
  isReadOnly: boolean
  deleteFloor: (floorId: string) => void
  employeeId: string
  loading: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <ListWrapper
        key={building.id}
        style={{ alignItems: 'center', cursor: 'pointer', justifyContent: 'space-between' }}
        onClick={() => setIsOpen(!isOpen)}>
        <div className={`flex items-center ${loading ? Classes.SKELETON : ''}`}>
          <div>
            <Icon
              className={loading ? Classes.SKELETON : ''}
              style={{ fontSize: 20, marginRight: 10 }}
              name={isOpen ? 'chevron-up' : 'chevron-down'}
            />
          </div>
          <Icon name="building" style={{ marginRight: 15 }} />
          <div>
            <div className={loading ? Classes.SKELETON : ''}>{building.name}</div>
            <div
              style={{ fontSize: 12 }}
              className={`text-grey-500 ${loading ? Classes.SKELETON : ''}`}>
              {building.address}
            </div>
          </div>
        </div>
        <div style={{ paddingRight: 16 }}>
          {building.floors.length} {pluralize(building.floors.length, 'Floor')}
        </div>
      </ListWrapper>

      <Collapse isOpen={isOpen}>
        {building.floors
          .sort(
            // ignore because sort works fine with strings
            (a, b) => (a.name < b.name) as unknown as number
          )
          .map((floor) => (
            <ListWrapper
              key={floor.id}
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingLeft: 78,
              }}>
              <span>{floor.name}</span>
              {!isReadOnly && (
                <IconButton
                  small
                  minimal
                  tooltip="Remove"
                  iconName="times"
                  className="icon-button"
                  onClick={() => {
                    deleteFloor(floor.id)
                    api.employee
                      .deleteFloorFromManagedLocations({
                        floorPlanId: floor.id,
                        id: employeeId,
                      })
                      .then(() => Toaster.success({ message: 'Floor deleted' }))
                      .catch((error: ApolloError) => Toaster.fail({ error }))
                  }}
                />
              )}
            </ListWrapper>
          ))}
      </Collapse>
    </>
  )
}

function useManagedLocations(
  employeeId: string,
  role: string
): IState & {
  addFloor: (floor: IFloorWithBuilding) => void
  deleteFloor: (floorId: string) => void
} {
  const [state, dispatch] = useReducer(reducer, {
    loading: true,
    groupedFloors: skeletonData,
    managedFloors: [],
  })

  useEffect(() => {
    dispatch({
      type: Actions.LOAD,
      groupedFloors: [],
      managedFloors: [],
    })
  }, [])

  useEffect(() => {
    const loadManagedLocations = async () => {
      try {
        const groupedFloors = await api.employee
          .managedLocations(employeeId)
          .then((groupedFloors) => {
            const groupedLocations = groupedFloors.reduce<IManagedBuilding[]>(
              (agg, { floorPlan: currentFloorplan }) => {
                const index = agg.findIndex(({ id }) => id === currentFloorplan.building.id)
                if (index > -1) {
                  agg[index].floors = [...agg[index].floors, currentFloorplan]
                  return agg
                } else {
                  return [...agg, { ...currentFloorplan.building, floors: [currentFloorplan] }]
                }
              },
              []
            )

            dispatch({
              type: Actions.LOAD,
              groupedFloors: groupBy(groupedLocations, 'region'),
              managedFloors: groupedFloors.map(({ floorPlan }) => floorPlan),
            })
          })
      } catch (error: any) {
        Toaster.fail({ error })
      }
    }
    if (employeeId) {
      loadManagedLocations()
    }
  }, [employeeId, dispatch])

  useEffect(() => {
    dispatch({
      type: Actions.LOAD,
      groupedFloors: [],
      managedFloors: [],
    })
  }, [role])

  return {
    ...state,
    addFloor: (floor: IFloorWithBuilding) => {
      let buildingExists = false
      let floorExists = false
      const justBuildings = flatten(Object.values(cloneDeep(state.groupedFloors)))

      justBuildings.forEach((building) => {
        if (floor.building.id === building.id) buildingExists = true
        building.floors.forEach((f) => {
          if (f.id === floor.id) floorExists = true
        })
      })

      if (!floorExists) {
        if (!buildingExists) {
          const updateState = [
            ...justBuildings,
            { ...floor.building, floors: [omit(floor, 'building')] },
          ]

          dispatch({
            type: Actions.UPDATE,
            groupedFloors: groupBy(updateState, 'region'),
            managedFloors: [...state.managedFloors, floor],
          })
        } else if (buildingExists) {
          const updateBuildingIndex = justBuildings.findIndex(({ id }) => id === floor.building.id)
          const updatedBuilding = justBuildings[updateBuildingIndex]

          updatedBuilding.floors = [
            ...updatedBuilding.floors,
            omit(floor, 'building'),
            // ignore because sort works fine with strings
          ].sort((a, b) => (a.name < b.name) as unknown as number)

          justBuildings[updateBuildingIndex] = updatedBuilding

          dispatch({
            type: Actions.UPDATE,
            groupedFloors: groupBy(justBuildings, 'region'),
            managedFloors: [...state.managedFloors, floor],
          })
        }
      }
    },

    deleteFloor: (floorId: string) => {
      const justBuildings = flatten(Object.values(cloneDeep(state.groupedFloors)))
      const updateBuildingIndex = justBuildings.findIndex(
        ({ floors }) => floors.findIndex(({ id }) => id === floorId) > -1
      )

      console.log(updateBuildingIndex, justBuildings[updateBuildingIndex])

      const updatedBuilding = justBuildings[updateBuildingIndex]

      if (updatedBuilding.floors.length === 1) {
        justBuildings.splice(updateBuildingIndex, 1)

        dispatch({
          type: Actions.UPDATE,
          groupedFloors: groupBy(justBuildings, 'region'),
          managedFloors: state.managedFloors.filter(({ id }) => floorId !== id),
        })
      } else {
        justBuildings[updateBuildingIndex].floors = justBuildings[
          updateBuildingIndex
        ].floors.filter(({ id }) => id !== floorId)

        dispatch({
          type: Actions.UPDATE,
          groupedFloors: groupBy(justBuildings, 'region'),
          managedFloors: state.managedFloors.filter(({ id }) => floorId !== id),
        })
      }
    },
  }
}

function reducer(state: IState, action: Action) {
  switch (action.type) {
    case Actions.LOAD:
      return {
        loading: false,
        groupedFloors: action.groupedFloors,
        managedFloors: action.managedFloors,
      }

    case Actions.UPDATE:
      return {
        ...state,
        groupedFloors: action.groupedFloors,
        managedFloors: action.managedFloors,
      }

    default:
      return state
  }
}

const skeletonData: { [region: string]: IManagedBuilding[] } = {
  AMER: [
    {
      id: '1',
      name: 'something',
      address: '123 a st',
      region: 'AMER',
      floors: [
        {
          id: '1',
          name: '1st Floor',
        },
      ],
    },
    {
      id: '2',
      name: 'something 2',
      address: '123 a st',
      region: 'AMER',
      floors: [
        {
          id: '1',
          name: '1st Floor',
        },
      ],
    },
  ],
}
